// src/pages/Teams.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AddTeamModal from "../components/AddTeamModal";
import EditTeamModal from "../components/EditTeamModal";
import TeamDetailsDialog from "../components/TeamDetailsDialog";
import TeamResultsModal from "../components/TeamResultsModal";
import { Pencil } from "lucide-react";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import Skeleton from "../components/ui/Skeleton";
import { loadCachedTeamsAndStations } from "../utils/loadCachedData";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import mergeStationsWithResultsAndEntries from "../utils/mergeStationsWithResultsAndEntries";
import useMetaWatcher from "../hooks/useMetaWatcher";
import { teamKey, gehoertZuTeam } from "../utils/teamKey";
import { istAusgesetzt } from "../utils/ausgesetzt";
import { toast } from "react-toastify";

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState("Alle");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [resultsTeam, setResultsTeam] = useState(null);
  const [suppressWatcher, setSuppressWatcher] = useState(false);

  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const role = localStorage.getItem("role");
  const firstLoadRef = useRef(true);

  // === Daten holen (Teams + Stationen + Ergebnisse) ===
  const fetchData = useCallback(
    async (options = {}) => {
      try {
        if (firstLoadRef.current) setLoading(true);

        const { teams, stations, results, fromCache } = await loadCachedTeamsAndStations(
          BACKEND_URL,
          navigate,
          options
        );

        console.log(
          fromCache
            ? "[Teams] Daten aus Cache geladen"
            : "[Teams] Daten frisch vom Backend geladen"
        );

        const resultsStations = Array.isArray(results?.stations) ? results.stations : [];

        // Stationen mit Ergebnissen mergen
        const mergedStations = mergeStationsWithResultsAndEntries(
          stations,
          resultsStations,
          []
        );

        setTeams(teams);
        setStations(mergedStations);

        if (firstLoadRef.current) {
          firstLoadRef.current = false;
          setLoading(false);
        }

        if (options.reloadTeams || options.reloadStations) {
          toast.info("Teams & Stationen aktualisiert");
          setSuppressWatcher(true);
          setTimeout(() => setSuppressWatcher(false), 3000);
        }
      } catch (err) {
        console.error("[Teams] Fehler beim Laden:", err);
        toast.error("Fehler beim Laden der Daten");
        setLoading(false);
      }
    },
    [BACKEND_URL, navigate]
  );

  // === MetaWatcher für automatische Updates ===
  useMetaWatcher(BACKEND_URL, navigate, ({ teams, stations, results }) => {
    if (suppressWatcher) {
      console.log("[MetaWatcher] Ignoriert – suppressed");
      return;
    }

    if (teams || stations) {
      console.log("[MetaWatcher] Struktur geändert → fetchData()");
      fetchData({ reloadTeams: teams, reloadStations: stations });
    } else if (results) {
      console.log("[MetaWatcher] Ergebnisse geändert → nur Ergebnisse neu laden");
      // Ergebnisse separat holen
      reloadResults();
    }
  });

  // === Nur Ergebnisse neu laden ===
  const reloadResults = useCallback(async () => {
    try {
      console.log("[Teams] reloadResults() gestartet");
      const data = await fetchWithAuth(`${BACKEND_URL}/results`, navigate);
      const resultsStations = Array.isArray(data?.stations) ? data.stations : [];

      setStations((prevStations) =>
        mergeStationsWithResultsAndEntries(prevStations, resultsStations, [])
      );
    } catch (err) {
      console.error("[Teams] Fehler beim Nachladen der Ergebnisse:", err);
    }
  }, [BACKEND_URL, navigate]);

  // === Initial Load ===
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // === Nach Änderung / Speichern neu laden ===
  const handleReloadAfterChange = useCallback(() => {
    console.log("[Teams] Reload nach Änderung ausgelöst");
    fetchData({ reloadTeams: true });
  }, [fetchData]);

  // === Nach dem Loeschen neu laden ===
  //
  // Zusaetzlich zu den Teams muessen die Stationen mit: das Loeschen entfernt
  // das Team auch aus deren Ergebnislisten und stoesst eine Neuberechnung an,
  // die die Punkte der uebrigen Teams veraendern kann.
  const handleReloadAfterDelete = useCallback(() => {
    setEditingTeam(null);
    fetchData({ reloadTeams: true, reloadStations: true, reloadResults: true });
  }, [fetchData]);

  // === Filter ===
  const filteredTeams = teams.filter((team) =>
    filterCategory === "Alle" ? true : team.event_type === filterCategory
  );

  
  const typeColors = {
    Männer: "badge-team-men",
    Frauen: "badge-team-women",
    Mixed: "badge-team-mixed",
  };

  // === UI ===
  if (loading) {
    return (
      <div className="p-4 bg-secondary-950 min-h-screen text-white">
        <h1 className="text-2xl font-bold mb-4">Teams</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      </div>
    );
  }

  // === Render ===
  return (
    <div className="p-4 bg-secondary-950 min-h-screen text-white">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-white">Teams</h1>
        {role === "admin" && (
          <Button
            onClick={() => {
              setEditingTeam(null);
              setShowAddModal(true);
            }}
          >
            Neu +
          </Button>
        )}
      </div>

      {/* Team Counter */}
      <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-gray-300">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-white">Gesamt:</span>
          <span>{teams.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-blue-400 font-semibold">Erwachsene:</span>
          <span>{teams.filter((t) => t.event_type === "Erwachsene").length}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-green-400 font-semibold">Kinder:</span>
          <span>{teams.filter((t) => t.event_type === "Kinder").length}</span>
        </div>
      </div>

      {/* Filter */}
      <div className="flex space-x-2 mb-4">
        {["Alle", "Kinder", "Erwachsene"].map((cat) => (
          <Button
            key={cat}
            variant={filterCategory === cat ? "primary" : "secondary"}
            onClick={() => setFilterCategory(cat)}
          >
            {cat}
          </Button>
        ))}
      </div>

      {/* Team Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {filteredTeams.length === 0 ? (
          <div className="col-span-full text-gray-500">Keine Teams gefunden.</div>
        ) : (
          filteredTeams.map((team, idx) => {
            // Nur Stationen zählen, die für diesen Teamtyp erlaubt sind
            const allowedStations = stations.filter((s) => {
              if (istAusgesetzt(s, team.event_type)) return false;
              const allowed = s.allow_groups ?? (s.allow_kids === false ? "Erwachsene" : "Alle");
              if (team.event_type === "Kinder") return allowed === "Kinder" || allowed === "Alle";
              if (team.event_type === "Erwachsene") return allowed === "Erwachsene" || allowed === "Alle";
              return true;
            });

            // Wie viele dieser erlaubten Stationen das Team schon erledigt hat
            const completedStations = allowedStations.filter(
              (s) =>
                Array.isArray(s.results) &&
                s.results.some((r) => gehoertZuTeam(r, team))
            ).length;

            const totalStations = allowedStations.length;
            const progressPct =
              totalStations > 0
                ? Math.round((completedStations / totalStations) * 100)
                : 0;


            const progressBgColor =
              team.event_type === "Kinder"
                ? "bg-progress-kids"
                : "bg-primary-500";

            return (
              <div
                key={teamKey(team) ?? idx}
                className="w-full max-w-full p-4 rounded shadow bg-secondary-900 border border-secondary-800 relative"
              >
                <div className="flex justify-between items-start">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold break-words text-white">
                        {team.team_name}
                      </h2>
                      <Badge
                        className={typeColors[team.team_type] ?? "badge-secondary"}
                      >
                        {team.team_type || "-"}
                      </Badge>
                    </div>

                    <p className="text-gray-400 text-sm">
                      Kategorie: {team.event_type}
                    </p>

                    <div className="mt-2">
                      <div className="w-full bg-secondary-800 rounded-full h-4">
                        <div
                          className={`h-4 rounded-full ${progressBgColor}`}
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                      <p className="text-sm mt-1 text-gray-400">
                        {completedStations} / {totalStations} Stationen erledigt (
                        {progressPct}%)
                      </p>
                    </div>

                    <div className="mt-4 flex gap-2">
                      <Button
                        variant="secondary"
                        onClick={() => setSelectedTeam(team)}
                      >
                        Details
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => setResultsTeam(team)}
                      >
                        Ergebnisse
                      </Button>
                    </div>
                  </div>

                  {role === "admin" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingTeam(team);
                        setShowAddModal(false);
                      }}
                      className="p-1 rounded hover:bg-secondary-800"
                      title="Team bearbeiten"
                      aria-label={`Team ${team.team_name} bearbeiten`}
                    >
                      <Pencil className="w-5 h-5 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modals */}
      <AddTeamModal
        open={showAddModal && !editingTeam}
        onClose={() => setShowAddModal(false)}
        onSaved={handleReloadAfterChange}
        existingTeams={teams}
      />

      <EditTeamModal
        open={!!editingTeam}
        onClose={() => setEditingTeam(null)}
        team={editingTeam}
        onSaved={handleReloadAfterChange}
        onDeleted={handleReloadAfterDelete}
      />

      <TeamDetailsDialog
        open={!!selectedTeam}
        onClose={() => setSelectedTeam(null)}
        team={selectedTeam}
      />

      <TeamResultsModal
        open={!!resultsTeam}
        onClose={() => setResultsTeam(null)}
        team={resultsTeam}
        stations={stations}
      />
    </div>
  );
}
